import { useState } from 'react'
import { useUpdateProject } from '../api/queries'
import type { Project } from '../api/types'

/**
 * Kundens eget sagsnummer, øverst på sagen ved siden af ordrenummeret
 * (beslutning 083).
 *
 * <p>
 * <b>Der står, hvor nummeret kommer fra.</b> Et dannet sagsnummer er en kopi af
 * vores eget ordrenummer, og tallet afslører det ikke selv - uden linjen ville
 * nogen skrive det i en mail til kunden i den tro, at kunden kender det.
 * </p>
 *
 * <p>
 * <b>Nummeret kan ryddes</b>, modsat ordrenummeret. Så falder det tilbage på
 * ordrenummeret, og man kan altid komme tilbage til udgangspunktet.
 * </p>
 */
export function CaseNumberControl({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const update = useUpdateProject(project.id)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Et dannet nummer staar ikke i feltet: det er ikke kundens, og det skal ikke
  // se ud som om nogen har skrevet det ind.
  const own = project.customerCaseNumberIsGenerated ? '' : (project.customerCaseNumber ?? '')

  function start() {
    setValue(own)
    setError(null)
    setEditing(true)
  }

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      await update.mutateAsync({ customerCaseNumber: value })
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sagsnummeret kunne ikke gemmes.')
    }
  }

  if (editing) {
    return (
      <form className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }} onSubmit={(e) => void save(e)}>
        <input
          aria-label="Kundens sagsnummer"
          placeholder="Kundens sagsnummer"
          autoFocus
          maxLength={50}
          style={{ width: 190 }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button className="btn btn--primary btn--sm" type="submit" disabled={update.isPending}>
          {update.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        <button className="btn btn--sm" type="button" onClick={() => setEditing(false)}>
          Fortryd
        </button>
        {error && <span className="tiny warn">{error}</span>}
        <span className="tiny muted" style={{ flexBasis: '100%' }}>
          Tomt felt = sagen kører på vores eget ordrenummer.
        </span>
      </form>
    )
  }

  return (
    <div className="row" style={{ gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'baseline' }}>
      {project.customerCaseNumber ? (
        project.customerCaseNumberIsGenerated ? (
          <span className="small muted">
            Sagsnr. {project.customerCaseNumber}{' '}
            <span className="tiny">(vores eget - kunden har ikke oplyst et)</span>
          </span>
        ) : (
          <span className="small muted">Kundens sagsnr. {project.customerCaseNumber}</span>
        )
      ) : (
        <span className="small muted">Intet sagsnummer</span>
      )}

      {canEdit && (
        <button className="btn btn--sm" type="button" onClick={start}>
          {project.customerCaseNumberIsGenerated || !project.customerCaseNumber ? 'Tilføj' : 'Ret'}
        </button>
      )}
    </div>
  )
}
